// Shared wire contract for story edit operations (client capture ↔ AI-server apply).
// See docs/specs/story-edit-operations.md and docs/adr/0003-id-anchored-list-operations.md.

export type JsonValue = null | boolean | number | string | JsonValue[] | { [key: string]: JsonValue };

/** Who owns an edited building block. Card references to globals are never owners. */
export type StoryEditElementOwner =
	| { kind: 'card'; cardId: string }
	| { kind: 'story' } // canonical global element
	| { kind: 'symbol-master'; symbolId: string }
	| { kind: 'symbol-instance'; symbolId: string; instanceId: string };

/** Authored document a change applies to, addressed by stable IDs only. */
export type StoryEditTarget =
	| { kind: 'element'; owner: StoryEditElementOwner; elementId: string }
	| { kind: 'card'; cardId: string } // card document: properties, root elements, events
	| { kind: 'step'; stepId: string } // step document: properties and its cards
	| { kind: 'settings' } // settings of the latest version
	| { kind: 'data' } // data of the latest version: steps, global elements, symbols
	| { kind: 'story' }; // story properties outside versions: name, language, …

/** RFC 6902 subset; `path` is a JSON Pointer relative to the target document. */
export type StoryPropertyPatch =
	| { op: 'add' | 'replace'; path: string; value: JsonValue }
	| { op: 'remove'; path: string };

/**
 * Ordered collections of a target: `steps` and `elements` of `data`, `cards` of a step,
 * `elements` (root blocks) of a card and `children` of an element.
 */
export type StoryEditListName = 'steps' | 'cards' | 'elements' | 'children';

export type StoryEditListAddress = { target: StoryEditTarget; list: StoryEditListName };

/**
 * ID-anchored list operation. Items are addressed by their item key (see `listItemKey`),
 * never by index; `after: null` means the start of the list.
 */
export type StoryListOperation =
	| { op: 'insert'; after: string | null; value: JsonValue } // a new item; moved-in items are not inside it
	| { op: 'move'; key: string; after: string | null; from?: StoryEditListAddress } // `from` omitted: same list
	| { op: 'remove'; key: string }; // the item and everything inside it

export type StoryPropertyChange = { target: StoryEditTarget; patch: StoryPropertyPatch[] };

export type StoryListChange = { target: StoryEditTarget; list: StoryEditListName; operations: StoryListOperation[] };

export type StoryEditChange = StoryPropertyChange | StoryListChange;

export const isListChange = (change: StoryEditChange): change is StoryListChange => 'operations' in change;

/** Operations a change carries: property patches or list operations. */
export const operationCountOf = (change: StoryEditChange) =>
	isListChange(change) ? change.operations.length : change.patch.length;

const isObject = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Identity of a list item: `_id` of a step, card or building block; a card's reference to a global
 * block by `$storyElement:<id>`; a symbol link by `$symbol:<instanceId>`. `undefined`: not addressable.
 */
export function listItemKey(item: unknown): string | undefined {
	if (!isObject(item)) return undefined;
	if (typeof item._id === 'string' && item._id) return item._id;
	if (typeof item.$storyElement === 'string') return `$storyElement:${item.$storyElement}`;
	const { symbol } = item;
	if (isObject(symbol) && typeof symbol.instanceId === 'string') return `$symbol:${symbol.instanceId}`;
	return undefined;
}

/** One captured user action; its changes are ordered and belong together. */
export type StoryEditAction = { actionId: string; changes: StoryEditChange[] };

/** The editor only edits the latest version, so the request carries no version key. */
export type StoryEditBatchRequest = { schemaVersion: 2; baseOpCounter: number; actions: StoryEditAction[] };

export type StoryEditSkipReason = 'target_missing' | 'container_missing' | 'item_exists' | 'item_missing';

/** `operation` is set for list operations; property changes are skipped whole. */
export type StoryEditSkip = { change: number; operation?: number; reason: StoryEditSkipReason };

export type StoryEditActionResult = { actionId: string; status: 'applied' | 'duplicate'; skipped: StoryEditSkip[] };

export type StoryEditBatchResponse = {
	status: 'applied';
	opCounter: number;
	behind: boolean;
	actions: StoryEditActionResult[];
};
